import {
  PREVIEW_FRAME_CAP,
  formatBytes,
  formatTime,
  state,
} from "./state.js"
import {
  chooseWorkerCount as baseChooseWorkerCount,
  estimateOutputBytes as baseEstimateOutputBytes,
  getFramePlan as getBaseFramePlan,
  refreshMetrics as baseRefreshMetrics,
} from "./frame-plan.js"
import {
  el,
  getOutputDimensions,
  getSettings,
} from "./settings.js"

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

function sampleEvenly(values, limit) {
  if (values.length <= limit) return values.slice()
  if (limit <= 1) return [values[0]]
  const result = []
  for (let index = 0; index < limit; index += 1) {
    const sourceIndex = Math.round(index * (values.length - 1) / (limit - 1))
    const value = values[sourceIndex]
    if (result.at(-1) !== value) result.push(value)
  }
  return result
}

function dominantSource(spec) {
  return Number(spec?.mix) >= 0.5 ? spec.to : spec.from
}

function applySmoothLoop(frames, settings) {
  if (!settings.smoothLoopEnabled || state.direction === "rebound" || frames.length < 4) return null
  const maxOverlap = Math.max(1, Math.floor(frames.length / 2) - 1)
  const overlap = clamp(Math.round(settings.smoothLoopFrames), 1, maxOverlap)
  const output = frames.map((frame) => ({ ...frame }))
  const start = output.length - overlap

  for (let index = 0; index < overlap; index += 1) {
    const ending = output[start + index]
    const opening = output[index]
    output[start + index] = {
      from: dominantSource(ending),
      to: dominantSource(opening),
      mix: (index + 1) / (overlap + 1),
      smoothLoop: true,
      morphFrame: true,
    }
  }

  return { frames: output, overlap }
}

export function getFramePlan(settings = getSettings()) {
  const base = getBaseFramePlan(settings)
  const smooth = applySmoothLoop(base.frames, settings)
  let frames = smooth?.frames || base.frames.slice()
  if (settings.maxFramesEnabled && frames.length > settings.maxFrames) frames = sampleEvenly(frames, settings.maxFrames)

  const animationDuration = frames.length / Math.max(1, base.outputFps)
  const outputDuration = animationDuration + settings.loopDelayMs / 1000
  const interpolatedFrames = frames.reduce((count, frame) => count + (frame.morphFrame ? 1 : 0), 0)

  return {
    ...base,
    frames,
    totalFrames: frames.length,
    morphFramesAdded: interpolatedFrames,
    interpolatedFrames,
    smoothLoopFramesUsed: smooth?.overlap || 0,
    smoothLoopApplied: Boolean(smooth),
    animationDuration,
    outputDuration,
    outputFps: base.outputFps,
    loopDelayMs: settings.loopDelayMs,
    delayMs: 1000 / Math.max(1, base.outputFps),
    wasCapped: base.wasCapped || frames.length < base.frames.length,
  }
}

export function getPreviewPlan(settings = getSettings()) {
  const output = getFramePlan(settings)
  const frames = sampleEvenly(output.frames, PREVIEW_FRAME_CAP)
  return {
    ...output,
    frames,
    totalFrames: frames.length,
    previewFrameDuration: output.animationDuration / Math.max(1, frames.length),
    previewWasReduced: frames.length < output.frames.length,
  }
}

export function chooseWorkerCount(dimensions = getOutputDimensions(), frameCount = getFramePlan().totalFrames, settings = getSettings()) {
  return baseChooseWorkerCount(dimensions, frameCount, settings)
}

export function estimateOutputBytes(dimensions, plan, settings = getSettings()) {
  return baseEstimateOutputBytes(dimensions, plan, settings)
}

export function refreshMetrics(statusOverride = null) {
  baseRefreshMetrics(statusOverride)
  const settings = getSettings()
  const plan = getFramePlan(settings)
  const dimensions = getOutputDimensions(settings)
  const workerCount = chooseWorkerCount(dimensions, plan.totalFrames, settings)

  if (el.loopDelayValue) el.loopDelayValue.textContent = `${settings.loopDelayMs} ms`
  if (el.smoothLoopFramesValue) el.smoothLoopFramesValue.textContent = `${settings.smoothLoopFrames} frames`
  if (el.motionBlurValue) el.motionBlurValue.textContent = `${settings.motionBlur}%`
  if (el.motionBlurHelper) el.motionBlurHelper.textContent = settings.motionBlur
    ? `Directional temporal blur inside the fixed ${plan.outputFps.toFixed(1)} fps output budget`
    : "Off"

  const unavailable = state.direction === "rebound"
  const active = settings.smoothLoopEnabled && plan.smoothLoopApplied
  if (el.smoothLoopToggleCard) el.smoothLoopToggleCard.classList.toggle("disabled-control", unavailable)
  if (el.smoothLoopFramesCard) el.smoothLoopFramesCard.classList.toggle("disabled-control", !settings.smoothLoopRequested || unavailable)
  if (el.smoothLoopFramesSlider) el.smoothLoopFramesSlider.disabled = !settings.smoothLoopRequested || unavailable
  if (el.smoothLoopHelper) {
    el.smoothLoopHelper.textContent = unavailable
      ? "Forward and Reverse only"
      : active
        ? `Crossfades the final ${plan.smoothLoopFramesUsed} existing frames into the beginning without increasing FPS`
        : settings.smoothLoopRequested
          ? "The selected clip is too short for this overlap"
          : "Off"
  }

  if (!state.file || !state.mediaReady) return

  const formatName = state.format === "gif" ? "GIF" : "WebP"
  el.statResolution.textContent = `${dimensions.width}×${dimensions.height}`
  el.statOutputFrames.textContent = `${plan.totalFrames}${plan.wasCapped ? " capped" : ""}`
  el.statVideoFrames.textContent = String(plan.sourceFrames)
  el.statSampledFrames.textContent = String(plan.sampledFrames)
  el.statMorphFrames.textContent = String(plan.interpolatedFrames)
  el.statOutputLength.textContent = formatTime(plan.outputDuration)
  el.statVideoLength.textContent = formatTime(plan.trimDuration)
  el.statOutputFps.textContent = plan.outputFps.toFixed(1)
  el.statEstimatedSize.textContent = `~${formatBytes(estimateOutputBytes(dimensions, plan, settings))}`
  el.workerPill.textContent = `Workers: ${workerCount}`
  el.fpsHelper.textContent = plan.wasCapped
    ? `Maximum frames reduced final output to ${plan.outputFps.toFixed(1)} fps`
    : `${plan.totalFrames} final frames at the selected ${plan.outputFps.toFixed(1)} fps; morphing does not multiply it`
  el.morphHelper.textContent = settings.morphAmount
    ? `${plan.interpolatedFrames} of ${plan.totalFrames} existing output frames interpolated; no extra frames added`
    : "Off"

  if (!statusOverride && !state.encoding) {
    const directionText = state.direction === "rebound" ? " · out + back" : ""
    const morphText = plan.interpolatedFrames ? ` · ${plan.interpolatedFrames} interpolated` : ""
    const blurText = settings.motionBlur ? ` · ${settings.motionBlur}% motion blur` : ""
    const smoothText = plan.smoothLoopApplied ? ` · ${plan.smoothLoopFramesUsed}-frame smooth loop` : ""
    const delayText = settings.loopDelayMs ? ` · ${settings.loopDelayMs} ms hold` : ""
    el.statusText.textContent = `${plan.sourceFrames} source → ${plan.sampledFrames} timeline samples → ${plan.totalFrames} ${formatName} at ${plan.outputFps.toFixed(1)} fps${directionText}${morphText}${blurText}${smoothText}${delayText}`
  }
}
